import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import Navbar from './components/Navbar';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Profile from './pages/Profile';
import Achievements from './pages/Achievements';
import Tasks from './pages/Tasks';
import AdminUsers from './pages/AdminUsers';
import AdminAchievements from './pages/AdminAchievements';
import AdminTasks from './pages/AdminTasks';
import AdminNotifications from './pages/AdminNotifications';
import AdminPositions from './pages/AdminPositions';
import HomeworksCheck from './pages/HomeworksCheck';
import PublicDashboard from './pages/PublicDashboard';
import PyA3 from './pages/PyA3';
import './styles/global.css';

const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading, user } = useAuth();

  if (isLoading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh'
      }}>
        Загрузка...
      </div>
    );
  }

  // Перенаправляем гостя сразу на public dashboard
  if (isAuthenticated && user?.role === 'guest') {
    return <Navigate to="/public" />;
  }

  return isAuthenticated ? <>{children}</> : <Navigate to="/login" />;
};

const AdminRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh'
      }}>
        Загрузка...
      </div>
    );
  }

  return isAuthenticated && user?.role === 'admin' ? <>{children}</> : <Navigate to="/dashboard" />;
};

const EngineeringManagerRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh'
      }}>
        Загрузка...
      </div>
    );
  }

  return isAuthenticated && (user?.role === 'engineering_manager' || user?.role === 'admin') ? <>{children}</> : <Navigate to="/dashboard" />;
};

const EngineerRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh'
      }}>
        Загрузка...
      </div>
    );
  }

  return isAuthenticated && (user?.role === 'engineer' || user?.role === 'engineering_manager' || user?.role === 'admin') ? <>{children}</> : <Navigate to="/public" />;
};

// НОВОЕ: Компонент для условного рендера навбара
const NavbarWrapper: React.FC = () => {
  const location = useLocation();
  
  // Скрываем навбар на этих путях
  const hideNavbarPaths = ['/public'];
  const showNavbar = !hideNavbarPaths.includes(location.pathname);
  
  return showNavbar ? <Navbar /> : null;
};

const AppContent: React.FC = () => {
  const { isAuthenticated, isLoading, user } = useAuth();

  // Показываем загрузочный экран во время проверки аутентификации
  if (isLoading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        fontSize: '18px'
      }}>
        Загрузка...
      </div>
    );
  }

  return (
    <Router>
      {/* ИЗМЕНЕНО: NavbarWrapper вместо <Navbar /> */}
      <NavbarWrapper />

      <Routes>
        {/* ПУБЛИЧНЫЙ МАРШРУТ - доступен без авторизации */}
        <Route path="/public" element={<PublicDashboard />} />
        <Route path="/login" element={<Login />} />

        {/* Если не аутентифицирован, перенаправляем на логин (кроме public) */}
        <Route path="*" element={
          !isAuthenticated ?
            <Navigate to="/login" /> :
            null
          } />
      </Routes>

      {/* ЗАЩИЩЕННЫЕ МАРШРУТЫ - только для аутентифицированных */}
      {isAuthenticated && (
        <Routes>
          <Route path="/" element={
            user?.role === 'guest' ?
              <Navigate to="/public" /> :
              <Navigate to="/dashboard" />
          } />
          <Route path="/dashboard" element={
            <EngineerRoute>
              <Dashboard />
            </EngineerRoute>
          } />
          <Route path="/profile" element={
            <EngineerRoute>
              <Profile />
            </EngineerRoute>
          } />
          <Route path="/achievements" element={
            <EngineerRoute>
              <Achievements />
            </EngineerRoute>
          } />
          <Route path="/tasks" element={
            <EngineerRoute>
              <Tasks />
            </EngineerRoute>
          } />
          <Route path="/admin/users" element={
            <AdminRoute>
              <AdminUsers />
            </AdminRoute>
          } />
          <Route path="/admin/achievements" element={
            <AdminRoute>
              <AdminAchievements />
            </AdminRoute>
          } />
          <Route path="/admin/positions" element={
            <AdminRoute>
              <AdminPositions />
            </AdminRoute>
          } />
          <Route path="/admin/tasks" element={
            <AdminRoute>
              <AdminTasks />
            </AdminRoute>
          } />
          <Route path="/admin/notifications" element={
            <AdminRoute>
              <AdminNotifications />
            </AdminRoute>
          } />
          <Route path="/admin/tasks-check" element={
            <EngineeringManagerRoute>
              <HomeworksCheck />
            </EngineeringManagerRoute>
          } />
          <Route path="/pya3" element={
            <EngineerRoute>
              <PyA3 />
            </EngineerRoute>
          } />
          {/* Для всех остальных маршрутов перенаправляем на дашборд */}
          <Route path="*" element={
            user?.role === 'guest' ?
              <Navigate to="/public" /> :
              <Navigate to="/dashboard" />
          } />
        </Routes>
      )}
    </Router>
  );
};

const App: React.FC = () => {
  return (
    <ThemeProvider>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </ThemeProvider>
  );
};

export default App;
